import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Row,
  Col,
  Card,
  CardBody,
  Input,
  Button,
  Table,
  Spinner,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import Select from "react-select";

import api from "../../../services/api";
import { rotuloDocumento } from "../../../utils/modeloDocumento";
import { useCompanies } from "../../../hooks/companies";
import { useAuth } from "../../../hooks/auth";
import { useToast } from "../../../hooks/toast";
import {
  fmtBRL,
  fmtInt,
  fmtData,
  fmtDataPura,
  fmtDataBRT,
  fmtDataHora,
  SITUACOES_FATURA,
  ABAS_CONFERENCIA,
  ABA_CONFERENCIA_PADRAO,
  colunasDaAba,
  colunasCompactasDaAba,
  labelSituacao,
  MOMENTOS,
  acoesDisponiveis,
  ACOES_CONCILIACAO,
  montarHtmlImpressao,
  rotuloAcao,
  bloqueioAcao,
  ordenarPendencias,
  montarCorpoLote,
} from "./formatters";

import {
  CtesModal,
  LancamentosModal,
  FaturaDetalheModal,
  ConciliacaoModal,
  ConciliacoesModal,
  EditarFaturaModal,
  DeletarFaturaModal,
  useDetalheCte,
  SITUACOES_EDITAVEIS,
  remarcarCtesDaFatura,
  situacaoReconferida,
  proximoPassoAposRecalculo,
} from "./modais";

import {
  autopilotBodyStyle,
  autopilotCardStyle,
  autopilotSecondaryButtonStyle,
} from "../../../components/autopilot/styles";
import PageHero from "../../../components/PageHero";
import FilterBand from "../../../components/FilterBand";
import SortHeader from "../../../components/SortHeader";
import TablePagination from "../../../components/TablePagination";

import "./styles.css";
// PO1-3160: selo de situação e alerta de vencimento compartilhados com o MeuFrete.
import { StatusBadge, getVencimentoAlert } from "./componentesCompartilhados";
import { pedidoDaUrl, temPermissao, urlAuditoriaPedido } from "../AuditoriaPedido/regras";
import NovaFaturaModal from "../CriarFatura/NovaFaturaModal";

// PO1-3307: quem edita a conferência cria a fatura manual (botão "Nova fatura").
const PERMISSAO_CRIAR_FATURA = "hub.fatura.conferencia.editar";

const selectStyles = { menu: (base) => ({ ...base, zIndex: 30 }) };

const DIVERGENCIA_OPTS = [
  { value: "", label: "Todos" },
  { value: "true", label: "Sim" },
  { value: "false", label: "Não" },
];

const PAGE_SIZES = [10, 20, 50, 100];

// Cor do "dot" de cada grupo (status-cards), seguindo os tokens de status.
const ABA_DOT_COLOR = {
  PROCESSAMENTO: "var(--everlog-status-warning)",
  AUDITORIA: "var(--everlog-status-info)",
  FECHAMENTO: "var(--everlog-status-success)",
  NEGOCIACAO: "var(--everlog-status-danger)",
  TODAS: "var(--everlog-status-neutral)",
};

// value (IdSituacaoFatura) → codigo (AVA/EVA/...), p/ somar contagens por grupo.
const CODIGO_POR_VALUE = SITUACOES_FATURA.reduce((acc, s) => {
  acc[s.value] = s.codigo;
  return acc;
}, {});

// Situações finalizadas (terminais) escondidas por padrão pelo toggle
// "Mostrar finalizadas": Cancelada (CAN=3), Integrada (PAG=8) e Paga (PGO=11).
const SITUACOES_FINALIZADAS = [3, 8, 11];

// Pills do filtro de data: escolhe QUAL data o range De/Até aplica.
const DATA_CAMPOS = [
  { value: "emissao", label: "Emissão" },
  { value: "entrada", label: "Entrada" },
  { value: "vencimento", label: "Vencimento" },
  { value: "integracao", label: "Integração" },
  // Dat_PagamentoReal (situação Paga, PO1-3653); o backend já filtra por ele (PO1-3656).
  { value: "pagamento", label: "Pagamento" },
];

const filtrosVazios = {
  dataCampo: "entrada",
  dataDe: "",
  dataAte: "",
  nrFaturaInt: "",
  nrFaturaExt: "",
  mostrarFinalizadas: false,
  abaKey: ABA_CONFERENCIA_PADRAO,
  pillSituacao: null,
  momento: null,
  divergencia: "",
  // PO1-3191: "Só as minhas" (Fatura.Id_UsuarioResponsavel = usuário logado).
  soMinhas: false,
  // PO1-3242: faturas com CT-e de NF deste pedido (NotaFiscal.Num_Pedido).
  pedido: "",
};

// A tela abre na aba "Em Processamento" (AVA) — o que o auditor olha primeiro —
// em vez de despejar todas as faturas. "Limpar" volta a esse estado.
const filtrosIniciais = { ...filtrosVazios };

// useHistory fora de um Router (testes que renderizam a tela sozinha) lança no
// react-router 5.1; sem router não há deep link e a tela segue normal.
function useHistorySeguro() {
  try {
    return useHistory();
  } catch (e) {
    return null;
  }
}

const DEFAULT_SORT = { field: "dataEmissaoExterna", dir: "desc" };

function LinhaResumo({ label, value }) {
  return (
    <div className="conf-fatura-drawer__row">
      <span className="conf-fatura-drawer__row-label">{label}</span>
      <span className="conf-fatura-drawer__row-value">{value || "—"}</span>
    </div>
  );
}

function SecaoResumo({ title, children }) {
  return (
    <div className="conf-fatura-drawer__section">
      <div className="conf-fatura-drawer__section-head">
        <span className="conf-fatura-drawer__section-title">{title}</span>
      </div>
      <div className="conf-fatura-drawer__section-body">{children}</div>
    </div>
  );
}

// Pendências da última conferência (Json_PendenciasConferencia do /detalhe).
// Estruturais em cartão vermelho no topo (nunca aprovam). As de frete por CT-e
// viram uma linha de resumo + tabela compacta ordenada pela maior diferença,
// com as primeiras PENDENCIAS_VISIVEIS e "Ver todas" (fatura com muitos CT-e).
const PENDENCIAS_VISIVEIS = 5;
const valorSemMoeda = (v) => fmtBRL(v).replace(/^R\$\s?/, "");

function PendenciasResumo({ conferencia, carregando, onAbrirCte }) {
  const [todas, setTodas] = useState(false);
  if (carregando) {
    return (
      <div className="conf-fatura-pendencias__vazio">
        <Spinner size="sm" /> Carregando…
      </div>
    );
  }
  const lista = ordenarPendencias(conferencia);
  if (!lista.length) {
    return (
      <div className="conf-fatura-pendencias__vazio">
        {conferencia ? "Sem pendências na última conferência." : "Ainda não conferida."}
      </div>
    );
  }
  const porCte = (p) => p.tipo !== "estrutural" && (p.idCte || p.numeroCte);
  const cartoes = lista.filter((p) => !porCte(p));
  const ctes = lista
    .filter(porCte)
    .sort((x, y) => {
      // Sem cálculo primeiro (não há o que comparar), depois a maior diferença.
      const semX = x.codigo === "FRETE_SEM_CALCULO" ? 1 : 0;
      const semY = y.codigo === "FRETE_SEM_CALCULO" ? 1 : 0;
      return semY - semX || Math.abs(Number(y.diferenca) || 0) - Math.abs(Number(x.diferenca) || 0);
    });
  // PO1-2256: CT-e marcado Não OK pelo auditor também é pendência por CT-e, mas
  // não tem diferença de frete: fica fora da soma e aparece como "Não OK".
  const divergentes = ctes.filter((p) => p.codigo === "FRETE_DIVERGENTE");
  const semCalculo = ctes.filter((p) => p.codigo === "FRETE_SEM_CALCULO").length;
  const naoOk = ctes.filter((p) => p.codigo === "CTE_NAO_OK").length;
  const somaDif = divergentes.reduce((acc, p) => acc + (Number(p.diferenca) || 0), 0);
  const visiveis = todas ? ctes : ctes.slice(0, PENDENCIAS_VISIVEIS);

  return (
    <div className="conf-fatura-pendencias-bloco">
      {cartoes.length > 0 && (
        <ul className="conf-fatura-pendencias">
          {cartoes.map((p, i) => (
            <li
              key={`${p.codigo}-${i}`}
              className={`conf-fatura-pendencia conf-fatura-pendencia--${
                p.tipo === "estrutural" ? "danger" : "warning"
              }`}
            >
              <span className="conf-fatura-pendencia__tipo">
                {p.tipo === "estrutural" ? "Estrutural" : "Frete"}
              </span>
              <span className="conf-fatura-pendencia__texto">{p.texto}</span>
            </li>
          ))}
        </ul>
      )}

      {ctes.length > 0 && (
        <>
          <div className="conf-fatura-pendencias__resumo">
            {divergentes.length > 0 && (
              <span>
                <b>{fmtInt(divergentes.length)}</b> CT-e com frete divergente ·{" "}
                <b className="conf-fatura-pendencias__dif">
                  {somaDif >= 0 ? "+" : "−"}
                  {fmtBRL(Math.abs(somaDif))}
                </b>
              </span>
            )}
            {semCalculo > 0 && (
              <span>
                <b>{fmtInt(semCalculo)}</b> CT-e sem cálculo
              </span>
            )}
            {naoOk > 0 && (
              <span>
                <b>{fmtInt(naoOk)}</b> CT-e marcado(s) Não OK
              </span>
            )}
          </div>
          <table className="conf-fatura-pendencias-tabela">
            <thead>
              <tr>
                <th>Documento</th>
                <th className="num">Cobrado</th>
                <th className="num">Calculado</th>
                <th className="num">Dif.</th>
              </tr>
            </thead>
            <tbody>
              {visiveis.map((p, i) => {
                const sem = p.codigo === "FRETE_SEM_CALCULO";
                const nok = p.codigo === "CTE_NAO_OK";
                const dif = Number(p.diferenca) || 0;
                const numero = p.numeroCte || p.idCte;
                return (
                  <React.Fragment key={`${p.codigo}-${p.idCte || p.numeroCte}-${i}`}>
                    <tr title={p.texto} className={p.motivo ? "tem-motivo" : undefined}>
                      <td>
                        {onAbrirCte && p.idCte ? (
                          <button
                            type="button"
                            className="conf-fatura-pendencias__cte"
                            onClick={() => onAbrirCte({ id: p.idCte, numero })}
                            title={`Abrir o detalhe do ${rotuloDocumento(p.modelo)}`}
                          >
                            {numero}
                          </button>
                        ) : (
                          numero
                        )}
                      </td>
                      <td className="num">{p.valorCobrado != null ? valorSemMoeda(p.valorCobrado) : "—"}</td>
                      <td className="num">{sem || nok || p.valorCalculado == null ? "—" : valorSemMoeda(p.valorCalculado)}</td>
                      <td className={`num ${sem || nok ? "is-sem-calculo" : "is-dif"}`}>
                        {sem ? "sem cálculo" : nok ? "Não OK" : `${dif >= 0 ? "+" : "−"}${valorSemMoeda(Math.abs(dif))}`}
                      </td>
                    </tr>
                    {p.motivo && (
                      <tr className="conf-fatura-pendencias__motivo-linha">
                        <td colSpan={4} className="conf-fatura-pendencias__motivo">
                          {p.motivo}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          {ctes.length > PENDENCIAS_VISIVEIS && (
            <button
              type="button"
              className="conf-fatura-pendencias__mais"
              onClick={() => setTodas((v) => !v)}
            >
              {todas ? "Mostrar menos" : `Ver todas (${fmtInt(ctes.length)})`}
            </button>
          )}
        </>
      )}
    </div>
  );
}

// Painel lateral (drawer) com o resumo da fatura — abre ao clicar na linha.
// PO1-3191: pendências, responsável e os botões da máquina de estados.
export function FaturaResumoLateral({
  fatura,
  detalhe,
  carregandoDetalhe,
  acoes,
  executando,
  erroAcao,
  onAcao,
  onClose,
  onVerDetalhes,
  onVerCtes,
  onAbrirCte,
}) {
  if (!fatura) return null;
  const f = fatura;
  const d = detalhe || {};
  const conferencia = d.conferencia || null;
  const responsavel = d.responsavelNome || f.responsavelNome;
  const estrutural = Boolean(
    f.pendenciaEstrutural || d.pendenciaEstrutural || (conferencia && conferencia.estrutural)
  );
  const faturaAcoes = { ...f, pendenciaEstrutural: estrutural };
  const listaAcoes = acoes || [];
  const bloqueios = listaAcoes.map((a) => bloqueioAcao(a, faturaAcoes)).filter(Boolean);
  return (
    <div
      className="conf-fatura-drawer card-everlog-shell"
      role="dialog"
      aria-label="Resumo da fatura"
    >
        <div className="conf-fatura-drawer__head">
          <div>
            <div className="conf-fatura-drawer__eyebrow">Resumo da fatura</div>
            <div className="conf-fatura-drawer__title">
              Fatura nº {f.numFaturaExterna || "—"}
            </div>
            <div className="conf-fatura-drawer__subtitle">
              Número Interno: {f.id}
            </div>
          </div>
          <div className="conf-fatura-drawer__head-acoes">
            <button
              type="button"
              className="conf-fatura-drawer__expandir"
              onClick={onVerDetalhes}
              title="Ver detalhes completos"
              aria-label="Ver detalhes completos"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="15 3 21 3 21 9" />
                <polyline points="9 21 3 21 3 15" />
                <line x1="21" y1="3" x2="14" y2="10" />
                <line x1="3" y1="21" x2="10" y2="14" />
              </svg>
            </button>
            <button
              type="button"
              className="conf-fatura-drawer__close"
              onClick={onClose}
              aria-label="Fechar"
            >
              ×
            </button>
          </div>
        </div>

        <div className="conf-fatura-drawer__meta">
          <StatusBadge codigo={f.situacaoCodigo}>
            {labelSituacao(f.situacaoCodigo, f.situacaoNome)}
          </StatusBadge>
          {f.transportadoraFantasia ? (
            <span className="conf-fatura-drawer__pill">{f.transportadoraFantasia}</span>
          ) : null}
        </div>

        <div className="conf-fatura-drawer__body">
          <SecaoResumo title="Pendências">
            <PendenciasResumo
              conferencia={conferencia}
              carregando={carregandoDetalhe}
              onAbrirCte={onAbrirCte}
            />
          </SecaoResumo>

          <SecaoResumo title="Identificação">
            <LinhaResumo label="Responsável" value={responsavel} />
            <LinhaResumo label="Momento" value={f.momentoFatura} />
            <LinhaResumo label="Remetente" value={f.remetenteFantasia} />
            <LinhaResumo label="Gerado por" value={f.geradoPor} />
          </SecaoResumo>

          <SecaoResumo title="CTE's">
            <LinhaResumo label="Quantidade" value={fmtInt(f.qtdCte)} />
            <LinhaResumo
              label="OK / NOK"
              value={`${fmtInt(f.qtdCteOk)} / ${fmtInt(f.qtdCteNok)}`}
            />
            <button
              type="button"
              className="conf-fatura-drawer__vercte"
              onClick={onVerCtes}
            >
              Ver CTE's
            </button>
          </SecaoResumo>

          <SecaoResumo title="Valores">
            <LinhaResumo label="Total Frete" value={fmtBRL(f.valTotalFrete)} />
            <LinhaResumo label="Total Débito" value={fmtBRL(f.valTotalDebito)} />
            <LinhaResumo label="Total Crédito" value={fmtBRL(f.valTotalCredito)} />
            <LinhaResumo label="Total Fatura" value={fmtBRL(f.valTotalFatura)} />
          </SecaoResumo>

          <SecaoResumo title="Datas">
            <LinhaResumo label="Dt. Emissão" value={fmtDataPura(f.dataEmissaoExterna)} />
            <LinhaResumo label="Dt. Entrada" value={fmtData(f.dataEmissaoInterna)} />
            <LinhaResumo label="Dt. Integração" value={fmtDataBRT(f.dataPagamento)} />
            {/* Data do "Marcar como paga" ou da baixa no ERP (Dat_PagamentoReal, PO1-3657). */}
            <LinhaResumo label="Dt. Pagamento" value={fmtDataBRT(f.dataPagamentoReal)} />
            <LinhaResumo label="Dt. Vencimento" value={fmtDataPura(f.dataVencimentoExterna)} />
          </SecaoResumo>
        </div>

        <div className="conf-fatura-drawer__actions">
          {listaAcoes.length > 0 && (
            <div className="conf-fatura-drawer__decisao conf-fatura-drawer__decisao--grid">
              {listaAcoes.map((a) => {
                const def = ACOES_CONCILIACAO[a] || {};
                const bloqueio = bloqueioAcao(a, faturaAcoes);
                const cor = ["success", "danger", "info", "warning", "primary"].includes(def.tom)
                  ? def.tom
                  : "default";
                return (
                  <Button
                    key={a}
                    color={cor}
                    outline={cor === "default"}
                    size="sm"
                    data-acao={a}
                    disabled={Boolean(bloqueio) || Boolean(executando)}
                    title={bloqueio || def.descricao || undefined}
                    onClick={() => onAcao(a)}
                  >
                    {executando === a ? <Spinner size="sm" /> : rotuloAcao(a, f.situacaoCodigo, f)}
                  </Button>
                );
              })}
            </div>
          )}
          {bloqueios.length > 0 && (
            <div className="conf-fatura-drawer__aviso" role="note">
              {bloqueios[0]}
            </div>
          )}
          {erroAcao && (
            <div className="conf-fatura-drawer__erro" role="alert">
              {erroAcao}
            </div>
          )}
          <Button block style={autopilotSecondaryButtonStyle} onClick={onVerDetalhes}>
            Ver detalhes completos
          </Button>
        </div>
    </div>
  );
}

// SortHeader agora é o componente compartilhado (components/SortHeader, PO1-2724).

function tableCellClass(col) {
  return [
    `conf-fatura-col--${col.key}`,
    col.num ? "num" : "",
    col.center ? "center" : "",
  ]
    .filter(Boolean)
    .join(" ");
}

// Ações em lote, na ordem do botão de ações. Aprovar e Validar agora ficam
// sempre na barra; as demais aparecem quando alguma fatura selecionada as tem.
const ACOES_EM_LOTE = [
  "VALIDAR_AGORA",
  "ASSUMIR",
  "ENCAMINHAR",
  "APROVAR",
  "DEVOLVER",
  "TRATATIVA",
  "LIBERAR",
  "ESTORNAR",
  "PAGAR",
  "MARCAR_PAGA",
  "CANCELAR",
];
const ACOES_LOTE_FIXAS = ["APROVAR", "VALIDAR_AGORA"];
const TITULO_LOTE = {
  APROVAR: "Só as faturas Em Análise ou Contestada sem pendência estrutural.",
  VALIDAR_AGORA: "Só as faturas A Validar, Em Tratativa ou Em Análise.",
};

// `location`/`history` vêm do <Route> (products.js); nos testes a tela é
// montada sem roteador, então os dois são opcionais.
const ConferenciaFatura = ({ location, history: historyRoute } = {}) => {
  const { empresa, transportadoraId } = useCompanies();
  const empresaId = empresa && empresa.value;

  const { user } = useAuth();
  const isTransportadora = user && user.setor === "transportadora";
  const isEmbarcador = user && (user.setor === "empresa" || user.setor === "admin");
  // Admin Everlog tem bypass (temPermissao), como no menu.
  const podeCriarFatura = Boolean(isEmbarcador && temPermissao(user, PERMISSAO_CRIAR_FATURA));
  const [novaFaturaAberta, setNovaFaturaAberta] = useState(false);

  // PO1-3242: vindo da Auditoria do Pedido (?pedido=X), abre em "Todas" com as
  // finalizadas, senão as faturas em análise do pedido ficariam fora da aba padrão.
  const pedidoInicial = pedidoDaUrl(location ? location.search : window.location.search);
  const [filtros, setFiltros] = useState(() =>
    pedidoInicial
      ? { ...filtrosIniciais, pedido: pedidoInicial, abaKey: "TODAS", mostrarFinalizadas: true }
      : filtrosIniciais
  );
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sort, setSort] = useState(DEFAULT_SORT);
  // Painel de filtros colapsado por padrão; abre pelo botão "Filtros" ao lado da busca.
  const [mostrarFiltros, setMostrarFiltros] = useState(Boolean(pedidoInicial));
  const gridRef = useRef(null);

  // Rola o grid pra vista (após filtrar por card/pill) — o usuário não precisa
  // descer manualmente. setTimeout garante que roda após o re-render.
  const scrollParaGrid = () => {
    setTimeout(() => {
      if (gridRef.current) {
        gridRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 60);
  };

  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totais, setTotais] = useState(null);
  const [resumoCounts, setResumoCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [consultado, setConsultado] = useState(false);
  const [exportando, setExportando] = useState(false);

  // Drill-down (H5): modal ativo + fatura selecionada + status de CTe.
  const [faturaSel, setFaturaSel] = useState(null);
  const [modalCtesStatus, setModalCtesStatus] = useState(undefined); // undefined = fechado
  // PO1-2256: detalhe do CT-e aberto pelo número nas pendências da lateral.
  // Depois de recalcular ali, remarca os CT-e da fatura pelo robô (que, em Em
  // Análise com pendência só de frete, já reconfere a fatura).
  const detalheCteLateral = useDetalheCte({
    onRecalculado: () => remarcarAposRecalculoLateral(),
  });
  const [modalLanc, setModalLanc] = useState(false);
  const [modalDetalhe, setModalDetalhe] = useState(false);
  const [faturaLateral, setFaturaLateral] = useState(null);

  // PO1-2199: deep link ?idFatura=<id> (link dos alertas de fatura). Lido uma
  // vez no mount, no padrão do ?abrir da ListagemCotacoes: a primeira busca sai
  // com o número interno e sem filtro de situação; quando a lista chega, a
  // fatura abre no painel lateral e a query some da URL.
  // O hook roda sempre (regra dos hooks); vale o history do <Route> quando vier.
  const historyHook = useHistorySeguro();
  const history = historyRoute || historyHook;
  const idFaturaUrlRef = useRef(
    history && history.location ? new URLSearchParams(history.location.search).get("idFatura") : null
  );

  // Clique na linha abre o painel lateral — exceto sobre links/dropdown/botões,
  // que mantêm seu próprio comportamento (situação→histórico, valores→modais…).
  const abrirResumoLateral = (f, e) => {
    if (
      e &&
      e.target &&
      e.target.closest &&
      e.target.closest("a, button, input, label, .conf-fatura-link, .dropdown, .dropdown-menu")
    ) {
      return;
    }
    setFaturaLateral(f);
  };

  const abrirCtes = (fatura, status) => {
    setFaturaSel(fatura);
    setModalCtesStatus(status || null);
  };
  const abrirLancamentos = (fatura) => {
    setFaturaSel(fatura);
    setModalLanc(true);
  };
  const abrirDetalhe = (fatura) => {
    setFaturaSel(fatura);
    setModalDetalhe(true);
  };

  // Conciliação (H6): ação selecionada + histórico.
  const [conciliacaoAcao, setConciliacaoAcao] = useState(null);
  const [modalHistorico, setModalHistorico] = useState(false);
  // PO1-3191: lote (faturas do modal em modo lote), ação direta em execução,
  // erro da ação no painel e retorno das ações para o usuário.
  const [conciliacaoLote, setConciliacaoLote] = useState(null);
  const [executandoAcao, setExecutandoAcao] = useState(null);
  const [erroAcao, setErroAcao] = useState(null);
  // Resultado de ação: toast padrão do Hub (canto superior, some sozinho),
  // em vez de um aviso fixo acima da tabela.
  const { addToast } = useToast();
  const setFeedback = useCallback(
    (f) => {
      if (!f || !addToast) return;
      const erro = f.tone === "danger";
      addToast({
        place: "tr",
        type: erro ? "danger" : "success",
        title: erro ? "Não foi possível concluir" : "Ação concluída",
        description: f.texto,
        icon: erro ? "tim-icons icon-alert-circle-exc" : "tim-icons icon-check-2",
      });
    },
    [addToast]
  );
  const [selecionadas, setSelecionadas] = useState(() => new Set());
  const [loteResultado, setLoteResultado] = useState(null);
  const [executandoLote, setExecutandoLote] = useState(false);
  const [detalheLateral, setDetalheLateral] = useState(null);
  const [carregandoDetalhe, setCarregandoDetalhe] = useState(false);
  const abrirConciliacao = (fatura, acao) => {
    setFaturaSel(fatura);
    setConciliacaoLote(null);
    setConciliacaoAcao(acao);
  };
  const abrirHistorico = (fatura) => {
    setFaturaSel(fatura);
    setModalHistorico(true);
  };

  // Editar (H9) + Imprimir (H9, via nova janela + window.print).
  const [modalEditar, setModalEditar] = useState(false);
  const abrirEditar = (fatura) => {
    setFaturaSel(fatura);
    setModalEditar(true);
  };

  const [modalDeletar, setModalDeletar] = useState(false);
  const abrirDeletar = (fatura) => {
    setFaturaSel(fatura);
    setModalDeletar(true);
  };

  const imprimirFatura = async (fatura) => {
    const win = window.open("", "_blank");
    if (win) win.document.write("Gerando impressão...");
    try {
      const [detRes, ctesRes] = await Promise.all([
        api.get(`/faturas/conferencia/${fatura.id}/detalhe`),
        api.get(`/faturas/conferencia/${fatura.id}/ctes`),
      ]);
      const det = (detRes.data && detRes.data.detalhe) || detRes.data || fatura;
      const html = montarHtmlImpressao(det, ctesRes.data || []);
      if (win) {
        win.document.open();
        win.document.write(html);
        win.document.close();
        win.focus();
        win.print();
      }
    } catch (e) {
      if (win) win.document.write("<p>Erro ao gerar impressão.</p>");
    }
  };

  const montarFiltros = useCallback(
    (filtrosArg) => {
      const f = filtrosArg || filtros;
      const params = {};
      if (f.dataCampo && (f.dataDe || f.dataAte)) {
        params.dataCampo = f.dataCampo;
        if (f.dataDe) params.dataDe = f.dataDe;
        if (f.dataAte) params.dataAte = f.dataAte;
      }
      if (f.nrFaturaInt) params.nrFaturaInt = f.nrFaturaInt;
      if (f.nrFaturaExt) params.nrFaturaExt = f.nrFaturaExt;
      if (f.pedido && f.pedido.trim()) params.pedido = f.pedido.trim();
      // Busca pelo número da fatura acha em qualquer situação, como o deep link
      // por ?idFatura=: ignora aba/pill e o toggle de finalizadas.
      const buscaPorNumero = !!(f.nrFaturaExt || f.nrFaturaInt);
      // Situação vem da aba/pill ativa: a pill estreita para um status; senão usa
      // o conjunto da aba. "Todas" (situacoes vazio) → sem filtro de situação.
      const aba = ABAS_CONFERENCIA.find((a) => a.key === f.abaKey);
      if (!buscaPorNumero) {
        if (f.pillSituacao) {
          params.idSituacoes = String(f.pillSituacao);
        } else if (aba && aba.situacoes.length) {
          params.idSituacoes = aba.situacoes.join(",");
        }
      }
      if (f.momento) params.momento = f.momento.value;
      // "Só as minhas": vale para a lista e para os contadores dos cards.
      if (f.soMinhas) params.soMinhas = 1;
      if (f.divergencia !== "") params.possuiDivergencia = f.divergencia;
      // Toggle "Mostrar finalizadas" — esconde Integrada/Cancelada por padrão.
      params.mostrarFinalizadas = f.mostrarFinalizadas || buscaPorNumero ? 1 : 0;
      // Transportadora selecionada no header global do HUB (matriz/filial é
      // resolvida no backend).
      if (transportadoraId) params.idTransportadora = transportadoraId;
      return params;
    },
    [filtros, transportadoraId]
  );

  const validarFiltros = useCallback(() => {
    if (
      filtros.dataDe &&
      filtros.dataAte &&
      filtros.dataDe > filtros.dataAte
    ) {
      return "A data inicial não pode ser maior que a data final.";
    }

    const camposNumericos = [
      ["nrFaturaInt", "Número Interno"],
      ["nrFaturaExt", "Buscar fatura"],
    ];

    const invalido = camposNumericos.find(([campo]) => {
      const valor = filtros[campo];
      return valor !== "" && Number(valor) <= 0;
    });

    if (invalido) {
      return `${invalido[1]} deve ser maior que zero.`;
    }

    return null;
  }, [filtros]);

  const buscar = useCallback(
    async (pagina, tamanho, ordenacao = sort, filtrosArg) => {
      setLoading(true);
      setError(null);
      try {
        const params = {
          ...montarFiltros(filtrosArg),
          page: pagina,
          pageSize: tamanho,
          sortField: ordenacao.field,
          sortDir: ordenacao.dir,
        };
        const res = await api.get("/faturas/conferencia", { params });
        setItems(res.data.items || []);
        setTotalCount(res.data.totalCount || 0);
        setTotais(res.data.totais || null);
        setConsultado(true);
      } catch (e) {
        setItems([]);
        setTotalCount(0);
        setTotais(null);
        setConsultado(true);
        setError(
          (e && e.response && e.response.data && e.response.data.error) ||
            e.message ||
            "Erro ao carregar faturas"
        );
      } finally {
        setLoading(false);
      }
    },
    [montarFiltros, sort]
  );

  // Contagens por situação para os status-cards (independem da aba selecionada;
  // refletem os demais filtros). Backend ignora o filtro de situação.
  const buscarResumo = useCallback(
    async (filtrosArg) => {
      try {
        const res = await api.get("/faturas/conferencia/resumo", {
          params: montarFiltros(filtrosArg),
        });
        setResumoCounts((res.data && res.data.counts) || {});
      } catch (e) {
        setResumoCounts({});
      }
    },
    [montarFiltros]
  );

  const handlePesquisar = useCallback(() => {
    const erroValidacao = validarFiltros();
    if (erroValidacao) {
      setError(erroValidacao);
      setItems([]);
      setTotalCount(0);
      setTotais(null);
      setConsultado(true);
      return;
    }
    setPage(1);
    buscar(1, pageSize);
    buscarResumo();
  }, [buscar, buscarResumo, pageSize, validarFiltros]);

  // Nova fatura (PO1-3307): fecha o modal e recarrega a lista pelo mesmo caminho
  // do deep link ?idFatura= — busca pelo número interno, em todas as situações,
  // e abre a fatura nova no painel lateral quando a lista chega.
  const handleFaturaCriada = (criada) => {
    setNovaFaturaAberta(false);
    setFeedback({ tone: "success", texto: `Fatura ${criada.numero} criada e enviada para a auditoria.` });
    if (criada.id === undefined || criada.id === null || !/^\d+$/.test(String(criada.id))) {
      buscar(1, pageSize);
      buscarResumo();
      return;
    }
    idFaturaUrlRef.current = String(criada.id);
    const f = { ...filtrosIniciais, nrFaturaInt: String(criada.id), mostrarFinalizadas: true, abaKey: null, pillSituacao: null };
    setFiltros(f);
    setPage(1);
    buscar(1, pageSize, sort, f);
    buscarResumo(f);
  };

  const handleCtesSalvos = (resultado, idFatura) => {
    // Mantém o painel aberto com os contadores persistidos. PO1-3191: a
    // marcação manual não muda a situação por si; em Em Análise sem CT-e Não
    // OK o robô reconfere na hora e pode devolver a situação nova (02/10/2026).
    const nova = resultado && resultado.reconferencia && resultado.reconferencia.situacaoNova;
    if (nova) {
      const fatura = [faturaSel, faturaLateral].find((f) => f && f.id === idFatura);
      setFeedback({
        tone: "success",
        texto: `Fatura ${(fatura && fatura.numFaturaExterna) || idFatura}: reconferida e passou para ${labelSituacao(nova)}.`,
      });
    }
    const atualizar = (f) =>
      f && f.id === idFatura ? { ...f, ...resultado } : f;
    setFaturaSel(atualizar);
    setFaturaLateral(atualizar);
    buscar(page, pageSize, sort, filtros);
    buscarResumo(filtros);
  };

  const handleLimpar = useCallback(() => {
    // "Só as minhas" fica fora do painel de filtros e sobrevive ao Limpar.
    const novo = { ...filtrosVazios, soMinhas: filtros.soMinhas };
    setFiltros(novo);
    setError(null);
    setFaturaLateral(null);
    setPage(1);
    buscar(1, pageSize, sort, novo);
    buscarResumo(novo);
  }, [buscar, buscarResumo, pageSize, sort, filtros.soMinhas]);

  // PO1-3191: "Só as minhas" refiltra cards + grid na hora.
  const toggleSoMinhas = useCallback(
    (valor) => {
      const novo = { ...filtros, soMinhas: valor };
      setFiltros(novo);
      setFaturaLateral(null);
      setPage(1);
      buscar(1, pageSize, sort, novo);
      buscarResumo(novo);
    },
    [filtros, buscar, buscarResumo, pageSize, sort]
  );

  // Busca ao vivo por nº da fatura (type-ahead). O debounce de 350 ms e o Enter
  // ficam no FilterBand (modo server): onSearchChange atualiza o filtro a cada
  // tecla e onSearchSubmit dispara a consulta. Respeita a aba ativa porque
  // montarFiltros já manda as situações do grupo selecionado. [PO1-2724]
  const handleBuscaFaturaChange = useCallback((valor) => {
    setFiltros((prev) => ({ ...prev, nrFaturaExt: valor }));
  }, []);
  const handleBuscaFaturaSubmit = useCallback(
    (valor) => {
      const novo = { ...filtros, nrFaturaExt: valor };
      setPage(1);
      buscar(1, pageSize, sort, novo);
      buscarResumo(novo);
    },
    [filtros, buscar, buscarResumo, pageSize, sort]
  );

  // Auto-filtro: "Mostrar finalizadas" Sim/Não refiltra cards + grid. Ao ligar,
  // mantém a aba; ao desligar numa pill finalizada (Integrada/Cancelada), solta.
  const toggleFinalizadas = useCallback(
    (valor) => {
      const pillFinalizada =
        filtros.pillSituacao && SITUACOES_FINALIZADAS.includes(filtros.pillSituacao);
      const novo = {
        ...filtros,
        mostrarFinalizadas: valor,
        pillSituacao: !valor && pillFinalizada ? null : filtros.pillSituacao,
      };
      setFiltros(novo);
      setFaturaLateral(null);
      setPage(1);
      buscar(1, pageSize, sort, novo);
      buscarResumo(novo);
    },
    [filtros, buscar, buscarResumo, pageSize, sort]
  );

  // Auto-filtro: trocar de aba refiltra na hora (zera a pill).
  const selecionarAba = useCallback(
    (abaKey) => {
      const novo = { ...filtros, abaKey, pillSituacao: null };
      setFiltros(novo);
      setFaturaLateral(null);
      setPage(1);
      buscar(1, pageSize, sort, novo);
      scrollParaGrid();
    },
    [filtros, buscar, pageSize, sort]
  );

  // Auto-filtro: clicar na pill estreita; clicar de novo na ativa, solta.
  const selecionarPill = useCallback(
    (situacao) => {
      const nova = filtros.pillSituacao === situacao ? null : situacao;
      const novo = { ...filtros, pillSituacao: nova };
      setFiltros(novo);
      setFaturaLateral(null);
      setPage(1);
      buscar(1, pageSize, sort, novo);
      scrollParaGrid();
    },
    [filtros, buscar, pageSize, sort]
  );

  const handleExportar = useCallback(async () => {
    const erroValidacao = validarFiltros();
    if (erroValidacao) {
      setError(erroValidacao);
      setConsultado(true);
      return;
    }

    setExportando(true);
    setError(null);
    try {
      const res = await api.get("/faturas/conferencia/export", {
        params: montarFiltros(),
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "conferencia-fatura.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(
        "Erro ao exportar. Verifique o limite de 50.000 registros ou refine os filtros."
      );
    } finally {
      setExportando(false);
    }
  }, [montarFiltros, validarFiltros]);

  // Recarrega ao trocar a empresa ativa (escopo de embarcador vem do JWT/contexto).
  useEffect(() => {
    if (empresaId) {
      setPage(1);
      const idUrl = idFaturaUrlRef.current;
      if (idUrl && /^\d+$/.test(idUrl)) {
        // Deep link: só o número interno, todas as situações (inclusive finalizadas).
        const f = { ...filtrosIniciais, nrFaturaInt: idUrl, mostrarFinalizadas: true, abaKey: null, pillSituacao: null };
        setFiltros(f);
        buscar(1, pageSize, sort, f);
      } else {
        idFaturaUrlRef.current = null;
        buscar(1, pageSize);
      }
      buscarResumo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaId, transportadoraId]);

  // Deep link: consome o ?idFatura= quando a lista carrega.
  useEffect(() => {
    const idUrl = idFaturaUrlRef.current;
    if (!idUrl || !consultado || loading) return;
    idFaturaUrlRef.current = null;
    const achada = items.find((f) => Number(f.id) === Number(idUrl));
    if (achada) {
      const aba =
        ABAS_CONFERENCIA.find((a) => a.pills.some((p) => p.key === achada.situacaoCodigo)) ||
        (achada.situacaoCodigo === "AVA" ? ABAS_CONFERENCIA[0] : null);
      if (aba) setFiltros((prev) => ({ ...prev, abaKey: aba.key }));
      setFaturaLateral(achada);
    } else {
      setFeedback({ tone: "danger", texto: `Fatura ${idUrl} não encontrada no seu escopo.` });
    }
    if (history && history.replace) history.replace({ pathname: history.location.pathname, search: "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, consultado, loading]);

  // Detalhe da fatura do painel (pendências da conferência + responsável).
  const idLateral = faturaLateral && faturaLateral.id;
  useEffect(() => {
    setErroAcao(null);
    if (!idLateral) {
      setDetalheLateral(null);
      return undefined;
    }
    let cancelado = false;
    setCarregandoDetalhe(true);
    setDetalheLateral(null);
    api
      .get(`/faturas/conferencia/${idLateral}/detalhe`)
      .then((res) => {
        if (!cancelado) setDetalheLateral((res.data && res.data.detalhe) || res.data || null);
      })
      .catch(() => {
        if (!cancelado) setDetalheLateral(null);
      })
      .finally(() => {
        if (!cancelado) setCarregandoDetalhe(false);
      });
    return () => {
      cancelado = true;
    };
  }, [idLateral]);

  // A seleção em lote vale para a página carregada.
  useEffect(() => {
    setSelecionadas(new Set());
  }, [items]);

  const recarregar = () => {
    buscar(page, pageSize);
    buscarResumo();
  };

  const mensagemApi = (e, fallback) =>
    (e && e.response && e.response.data && e.response.data.error) ||
    (e && e.message) ||
    fallback;

  const remarcarAposRecalculoLateral = async () => {
    const fatura = faturaLateral;
    if (!fatura || !SITUACOES_EDITAVEIS.includes(fatura.situacaoCodigo)) return;
    const numero = fatura.numFaturaExterna || fatura.id;
    try {
      const resposta = await remarcarCtesDaFatura(fatura.id);
      const nova = situacaoReconferida(resposta);
      const passo = proximoPassoAposRecalculo(fatura.situacaoCodigo, resposta);
      setFeedback({
        tone: "success",
        texto: `Fatura ${numero}: marcações dos CT-e atualizadas.${passo ? ` ${passo}` : ""}`,
      });
      if (nova) setFaturaLateral(null);
      recarregar();
    } catch (e) {
      setFeedback({
        tone: "danger",
        texto: `Fatura ${numero}: as marcações OK/NOK não foram atualizadas (${mensagemApi(e, "erro no robô")}).`,
      });
    }
  };

  // Resultado de uma ação numa fatura (modal ou direta).
  const concluirAcao = (fatura, acao, resultado) => {
    const nova = resultado && resultado.situacaoNova;
    // PO1-3469: "Enviar ao financeiro" diz para quem o e-mail foi.
    const email = resultado && resultado.emailFinanceiro;
    const destinos = email && Array.isArray(email.destinatarios) ? email.destinatarios.join(", ") : "";
    setFeedback({
      tone: "success",
      texto: `Fatura ${fatura.numFaturaExterna || fatura.id}: ${rotuloAcao(acao, fatura.situacaoCodigo, fatura)} concluído${
        nova ? ` · ${labelSituacao(nova)}` : ""
      }${destinos ? ` · e-mail enviado para ${destinos}` : ""}.`,
    });
    setFaturaLateral(null);
    recarregar();
  };

  // VALIDAR_AGORA e ASSUMIR executam direto; as demais abrem o modal.
  const executarAcao = async (fatura, acao) => {
    const def = ACOES_CONCILIACAO[acao] || {};
    if (bloqueioAcao(acao, fatura)) return;
    if (!def.direta) {
      abrirConciliacao(fatura, acao);
      return;
    }
    setExecutandoAcao(acao);
    setErroAcao(null);
    setFeedback(null);
    try {
      const res = await api.post(`/faturas/conferencia/${fatura.id}/conciliar`, { acao });
      concluirAcao(fatura, acao, res && res.data);
    } catch (e) {
      const msg = mensagemApi(e, "Erro ao executar a ação");
      setErroAcao(msg);
      setFeedback({
        tone: "danger",
        texto: `Fatura ${fatura.numFaturaExterna || fatura.id}: ${msg}`,
      });
    } finally {
      setExecutandoAcao(null);
    }
  };

  // ─── Seleção em lote (padrão do CtesValidacao) ────────────────────────────
  const acoesDaLinha = (f) => acoesDisponiveis(f.situacaoCodigo, { isEmbarcador, isTransportadora });
  // Em lote valem as mesmas ações do botão de ações da linha (menos Contestar,
  // que é da transportadora). Cada fatura só entra nas ações que tem.
  const podeEmLote = (acao, f) => acoesDaLinha(f).includes(acao) && !bloqueioAcao(acao, f);
  const selecionavel = (f) => ACOES_EM_LOTE.some((a) => podeEmLote(a, f));

  const idsSelecionaveis = items.filter(selecionavel).map((f) => f.id);
  const nSelecionadasVisiveis = idsSelecionaveis.filter((id) => selecionadas.has(id)).length;
  const todasSelecionadas =
    idsSelecionaveis.length > 0 && nSelecionadasVisiveis === idsSelecionaveis.length;
  const faturasSelecionadas = items.filter((f) => selecionadas.has(f.id));
  const lotePorAcao = ACOES_EM_LOTE.reduce((acc, a) => {
    acc[a] = faturasSelecionadas.filter((f) => podeEmLote(a, f));
    return acc;
  }, {});

  const alternarSelecao = (id) =>
    setSelecionadas((prev) => {
      const novo = new Set(prev);
      if (novo.has(id)) novo.delete(id);
      else novo.add(id);
      return novo;
    });

  const alternarTodas = () =>
    setSelecionadas((prev) => {
      const novo = new Set(prev);
      if (todasSelecionadas) idsSelecionaveis.forEach((id) => novo.delete(id));
      else idsSelecionaveis.forEach((id) => novo.add(id));
      return novo;
    });

  const registrarLote = (acao, faturasLote, resultados) => {
    const porId = faturasLote.reduce((acc, f) => {
      acc[f.id] = f;
      return acc;
    }, {});
    setLoteResultado({
      acao,
      itens: (resultados || []).map((r) => ({
        ...r,
        numero: (porId[r.idFatura] && porId[r.idFatura].numFaturaExterna) || r.idFatura,
      })),
    });
    setSelecionadas(new Set());
    setFaturaLateral(null);
    recarregar();
  };

  // Ação direta (Validar agora, Assumir) vai direto ao lote; as demais abrem o
  // modal em modo lote com os campos da ação.
  const executarLote = async (acao) => {
    const faturasLote = lotePorAcao[acao] || [];
    if (!faturasLote.length) return;
    const def = ACOES_CONCILIACAO[acao] || {};
    if (!def.direta) {
      setFaturaSel(null);
      setConciliacaoLote(faturasLote);
      setConciliacaoAcao(acao);
      return;
    }
    setExecutandoLote(true);
    setLoteResultado(null);
    try {
      const form = montarCorpoLote(
        acao,
        faturasLote.map((f) => f.id)
      );
      const res = await api.post("/faturas/conferencia/lote", form, {
        headers: { "content-type": "multipart/form-data" },
      });
      registrarLote(acao, faturasLote, (res.data && res.data.resultados) || []);
    } catch (e) {
      setFeedback({ tone: "danger", texto: mensagemApi(e, `Erro ao executar ${def.label || acao} em lote`) });
    } finally {
      setExecutandoLote(false);
    }
  };

  const irParaPagina = useCallback(
    (novaPagina) => {
      setPage(novaPagina);
      buscar(novaPagina, pageSize);
    },
    [buscar, pageSize]
  );

  const handleOrdenar = useCallback(
    (field) => {
      const nextSort =
        sort.field === field
          ? { field, dir: sort.dir === "asc" ? "desc" : "asc" }
          : { field, dir: "asc" };
      setSort(nextSort);
      setPage(1);
      buscar(1, pageSize, nextSort);
    },
    [buscar, pageSize, sort]
  );

  const setFiltro = (campo, valor) =>
    setFiltros((prev) => ({ ...prev, [campo]: valor }));

  const abaAtiva =
    ABAS_CONFERENCIA.find((a) => a.key === filtros.abaKey) || ABAS_CONFERENCIA[0];
  const pillAtiva = abaAtiva.pills.find((p) => p.situacao === filtros.pillSituacao);
  const labelStatusAtivo = pillAtiva ? pillAtiva.label : abaAtiva.label;
  const descricaoStatusAtivo = pillAtiva ? pillAtiva.copy : abaAtiva.copy;
  // Esconde pills de situações finalizadas quando o toggle está em Não.
  const pillsVisiveis = abaAtiva.pills.filter(
    (p) => filtros.mostrarFinalizadas || !SITUACOES_FINALIZADAS.includes(p.situacao)
  );

  // Quantos filtros avançados estão ativos (badge no cabeçalho do painel).
  const filtrosAtivosCount =
    (filtros.dataDe || filtros.dataAte ? 1 : 0) +
    (filtros.nrFaturaInt ? 1 : 0) +
    (filtros.pedido && filtros.pedido.trim() ? 1 : 0) +
    (filtros.momento ? 1 : 0) +
    (filtros.divergencia !== "" ? 1 : 0) +
    (filtros.mostrarFinalizadas ? 1 : 0);

  // Soma das contagens (do /resumo) das situações de um grupo, para o card.
  const contarGrupo = (aba) => {
    if (aba.key === "TODAS") {
      return Object.values(resumoCounts).reduce((a, b) => a + (Number(b) || 0), 0);
    }
    return aba.situacoes.reduce(
      (sum, value) => sum + (resumoCounts[CODIGO_POR_VALUE[value]] || 0),
      0
    );
  };

  // Dropdown de ações de uma fatura (coluna "acoes").
  const renderAcoes = (f) => {
    const acoes = acoesDaLinha(f);
    const podeEditar = isEmbarcador && f.situacaoCodigo === "AVA";
    const podeDeletar =
      isEmbarcador && ["AVA", "EVA", "CON", "CAN", "BLO"].includes(f.situacaoCodigo);
    return (
      <UncontrolledDropdown>
        <DropdownToggle
          size="sm"
          color="primary"
          className="conf-fatura-actions-toggle"
        >
          <i className="tim-icons icon-settings-gear-63" />
        </DropdownToggle>
        <DropdownMenu className="conf-fatura-actions-menu">
          {/* PO1-3749: o detalhe também fica no menu de ações, no topo. */}
          <DropdownItem className="conf-fatura-actions-detalhe" onClick={() => abrirDetalhe(f)}>
            Ver detalhes da fatura
          </DropdownItem>
          <DropdownItem divider />
          {acoes.map((a) => {
            const bloqueio = bloqueioAcao(a, f);
            return (
              <DropdownItem
                key={a}
                disabled={Boolean(bloqueio) || Boolean(executandoAcao)}
                title={bloqueio || undefined}
                onClick={() => executarAcao(f, a)}
              >
                {rotuloAcao(a, f.situacaoCodigo, f)}
              </DropdownItem>
            );
          })}
          {podeEditar && (
            <DropdownItem onClick={() => abrirEditar(f)}>Editar Fatura</DropdownItem>
          )}
          {podeDeletar && (
            <DropdownItem onClick={() => abrirDeletar(f)}>Deletar Fatura</DropdownItem>
          )}
          <DropdownItem onClick={() => imprimirFatura(f)}>Imprimir</DropdownItem>
        </DropdownMenu>
      </UncontrolledDropdown>
    );
  };

  // PO1-3191: resumo da pendência (vermelho quando estrutural).
  const renderPendencia = (f) => {
    if (!f.pendenciaResumo) return <span className="conf-fatura-muted">—</span>;
    const tone = f.pendenciaEstrutural ? "danger" : "warning";
    return (
      <span
        className={`conf-fatura-pendencia-cell conf-fatura-pendencia-cell--${tone}`}
        title={f.pendenciaEstrutural ? "Pendência estrutural: não pode ser aprovada" : undefined}
      >
        {f.pendenciaResumo}
      </span>
    );
  };

  const renderDivergenciaStatus = (f) => {
    const hasDivergencia = Boolean(f.divergencia);
    const tone = hasDivergencia ? "danger" : "success";
    const label = hasDivergencia ? "Sim" : "Não";
    const title = hasDivergencia ? "Com divergência" : "Sem divergência";

    return (
      <span
        className={`conf-fatura-divergencia-status conf-fatura-divergencia-status--${tone}`}
        title={title}
        aria-label={title}
      >
        <span className={`conf-fatura-dot conf-fatura-dot--${tone}`} />
        <span>{label}</span>
      </span>
    );
  };

  const renderVencimento = (f) => {
    const alert = getVencimentoAlert(f);

    return (
      <span className="conf-fatura-vencimento-cell">
        <span>{fmtDataPura(f.dataVencimentoExterna)}</span>
        {alert ? (
          <i
            className={`tim-icons icon-alert-circle-exc conf-fatura-vencimento-alert conf-fatura-vencimento-alert--${alert.tone}`}
            title={alert.label}
            aria-label={alert.label}
          />
        ) : null}
      </span>
    );
  };

  // Registry de colunas do grid: label, ordenação, alinhamento, total e célula.
  const COLUNAS = {
    selecao: {
      label: (
        <input
          type="checkbox"
          className="conf-fatura-sel"
          aria-label="Selecionar todas as faturas da página"
          checked={todasSelecionadas}
          disabled={idsSelecionaveis.length === 0}
          ref={(el) => {
            if (el) el.indeterminate = nSelecionadasVisiveis > 0 && !todasSelecionadas;
          }}
          onChange={alternarTodas}
        />
      ),
      center: true,
      cell: (f) => (
        <input
          type="checkbox"
          className="conf-fatura-sel"
          aria-label={`Selecionar fatura ${f.numFaturaExterna || f.id}`}
          checked={selecionadas.has(f.id)}
          disabled={!selecionavel(f)}
          title={selecionavel(f) ? undefined : "Sem ação em lote nesta situação"}
          onChange={() => alternarSelecao(f.id)}
        />
      ),
    },
    acoes: { label: "Ações", cell: (f) => renderAcoes(f) },
    pendencia: { label: "Pendência", cell: (f) => renderPendencia(f) },
    responsavel: { label: "Responsável", cell: (f) => f.responsavelNome || "—" },
    momento: { label: "Momento", cell: (f) => f.momentoFatura },
    nrInterno: {
      label: "Nr. Interno",
      sortField: "id",
      cell: (f) => (
        <button
          type="button"
          className="conf-fatura-link"
          title="Ver detalhes da fatura"
          onClick={(e) => {
            e.stopPropagation();
            abrirDetalhe(f);
          }}
        >
          {f.id}
          <i className="tim-icons icon-zoom-split" aria-hidden="true" />
        </button>
      ),
    },
    nrExterno: {
      label: "Nr. Fatura",
      sortField: "numFaturaExterna",
      cell: (f) => f.numFaturaExterna,
    },
    situacao: {
      label: "Situação",
      sortField: "situacao",
      cell: (f) => (
        <span className="conf-fatura-link" onClick={() => abrirHistorico(f)}>
          <StatusBadge codigo={f.situacaoCodigo}>
            {labelSituacao(f.situacaoCodigo, f.situacaoNome)}
          </StatusBadge>
        </span>
      ),
    },
    qtdCte: {
      label: "Qt. documentos",
      sortField: "qtdCte",
      num: true,
      total: (t) => fmtInt(t.qtdCte),
      cell: (f) => (
        <span className="conf-fatura-link" onClick={() => abrirCtes(f, null)}>
          {fmtInt(f.qtdCte)}
        </span>
      ),
    },
    divergencia: {
      label: "Divergência",
      sortField: "divergencia",
      center: true,
      cell: (f) => renderDivergenciaStatus(f),
    },
    totalFrete: {
      label: "Total Frete",
      sortField: "valTotalFrete",
      num: true,
      total: (t) => fmtBRL(t.frete),
      cell: (f) => fmtBRL(f.valTotalFrete),
    },
    totalDebito: {
      label: "Total Débito",
      sortField: "valTotalDebito",
      num: true,
      total: (t) => fmtBRL(t.debito),
      cell: (f) => (
        <span className="conf-fatura-link" onClick={() => abrirLancamentos(f)}>
          {fmtBRL(f.valTotalDebito)}
        </span>
      ),
    },
    totalCredito: {
      label: "Total Crédito",
      sortField: "valTotalCredito",
      num: true,
      total: (t) => fmtBRL(t.credito),
      cell: (f) => (
        <span className="conf-fatura-link" onClick={() => abrirLancamentos(f)}>
          {fmtBRL(f.valTotalCredito)}
        </span>
      ),
    },
    totalFatura: {
      label: "Total Fatura",
      sortField: "valTotalFatura",
      num: true,
      total: (t) => fmtBRL(t.fatura),
      cell: (f) => fmtBRL(f.valTotalFatura),
    },
    transportadora: {
      label: "Transportadora",
      sortField: "transportadora",
      cell: (f) => f.transportadoraFantasia,
    },
    cteOk: {
      label: "Documentos OK",
      num: true,
      cell: (f) => (
        <span className="conf-fatura-link" onClick={() => abrirCtes(f, "OOK")}>
          {fmtInt(f.qtdCteOk)}
        </span>
      ),
    },
    cteNok: {
      label: "Documentos NOK",
      num: true,
      cell: (f) => (
        <span className="conf-fatura-link" onClick={() => abrirCtes(f, "NOK")}>
          {fmtInt(f.qtdCteNok)}
        </span>
      ),
    },
    dtEmissaoInt: {
      label: "Dt. Emissão Int.",
      sortField: "dataEmissaoInterna",
      cell: (f) => fmtDataHora(f.dataEmissaoInterna),
    },
    // Data de emissão da fatura. Se a transportadora não enviou a data externa,
    // usa a entrada no FLOW como fallback.
    dtEntrada: {
      label: "Dt. Emissão",
      sortField: "dataEmissaoExterna",
      cell: (f) =>
        f.dataEmissaoExterna
          ? fmtDataPura(f.dataEmissaoExterna)
          : fmtData(f.dataEmissaoInterna),
    },
    dtVencPrev: {
      label: "Dt. Venc. Prev.",
      sortField: "dataVencimentoPrevista",
      cell: (f) => fmtDataPura(f.dataVencimentoPrevista),
    },
    dtVencimento: {
      label: "Dt. Vencimento",
      sortField: "dataVencimentoExterna",
      cell: (f) => renderVencimento(f),
    },
    geradoPor: { label: "Gerado por", cell: (f) => f.geradoPor },
    embarcador: { label: "Embarcador", cell: (f) => f.embarcadorFantasia },
    remetente: { label: "Remetente", cell: (f) => f.remetenteFantasia },
  };

  // Com o resumo lateral aberto o grid encolhe para as colunas compactas.
  // PO1-3191: o embarcador ganha a coluna de seleção em lote na frente.
  const chavesColunas = faturaLateral
    ? colunasCompactasDaAba(filtros.abaKey)
    : colunasDaAba(filtros.abaKey);
  const colunasAtivas = (isEmbarcador ? ["selecao", ...chavesColunas] : chavesColunas)
    .map((k) => ({ key: k, ...COLUNAS[k] }))
    .filter((c) => c.label);

  return (
    <div className="content conf-fatura-page">
      <Row>
        <Col>
          {/* Hero padrão do Hub (PO1-2724): mesmo visual de antes, agora via PageHero. */}
          <PageHero
            eyebrow="Operação"
            title="Auditoria de Faturas"
            description="Conferência de CT-es, divergências e conciliação das faturas do FLOW."
            actions={
              <>
                <Button
                  className="btn-sm"
                  style={autopilotSecondaryButtonStyle}
                  onClick={handlePesquisar}
                  disabled={loading}
                >
                  {loading ? <Spinner size="sm" /> : "↺ Atualizar"}
                </Button>
                {podeCriarFatura ? (
                  <Button className="btn-sm" color="primary" onClick={() => setNovaFaturaAberta(true)}>
                    <i className="tim-icons icon-simple-add" style={{ marginRight: 6 }} />
                    Nova fatura
                  </Button>
                ) : null}
                <Button
                  className="btn-sm"
                  color="success"
                  onClick={handleExportar}
                  disabled={loading || exportando}
                >
                  {exportando ? <Spinner size="sm" /> : "Exportar Excel"}
                </Button>
              </>
            }
          />

          <Card
            className="card-everlog-shell"
            style={{
              ...autopilotCardStyle,
              marginTop: "1.25rem",
              background: "var(--everlog-card-hero-bg)",
            }}
          >
            <CardBody style={autopilotBodyStyle}>
              <div className="conf-fatura-control-panel">
                {/* Faixa de busca padrão do Hub (PO1-2724). O painel de filtros desta tela
                    continua no grid abaixo; o botão "Filtros" só alterna mostrarFiltros. */}
                <FilterBand
                  mode="server"
                  withCard={false}
                  searchLabel="Buscar fatura"
                  searchHint="Nº externo nesta visão"
                  searchPlaceholder="Digite o nº da fatura"
                  searchType="text"
                  searchValue={filtros.nrFaturaExt}
                  onSearchChange={handleBuscaFaturaChange}
                  onSearchSubmit={handleBuscaFaturaSubmit}
                  activeFiltersCount={filtrosAtivosCount}
                  open={mostrarFiltros}
                  onToggle={setMostrarFiltros}
                  showFiltersButton
                  style={{ "--hub-fb-label-max": "210px" }}
                />

                <div className="conf-fatura-control-grid">
                  <section
                    ref={gridRef}
                    className="conf-fatura-control-section conf-fatura-control-section--status"
                  >
                    <div className="conf-fatura-control-section__head">
                      <div>
                        <div className="conf-fatura-control-section__title">Status</div>
                        <div className="conf-fatura-control-section__subtitle">
                          Selecione uma etapa para filtrar a fila.
                        </div>
                      </div>
                      <label className="conf-fatura-sominhas">
                        <input
                          type="checkbox"
                          name="soMinhas"
                          checked={filtros.soMinhas}
                          onChange={(e) => {
                            const marcado = e.target.checked;
                            toggleSoMinhas(marcado);
                          }}
                          disabled={loading}
                        />
                        <span>Só as minhas</span>
                      </label>
                    </div>

                    <div className="conf-fatura-status-grid">
                      {ABAS_CONFERENCIA.map((aba) => {
                        const ativo = aba.key === filtros.abaKey;
                        const statusColor = ABA_DOT_COLOR[aba.key];
                        return (
                          <button
                            key={aba.key}
                            type="button"
                            className={`conf-fatura-status-card${ativo ? " is-active" : ""}`}
                            style={{ "--conf-fatura-status-color": statusColor }}
                            onClick={() => selecionarAba(aba.key)}
                            disabled={loading}
                          >
                            <span
                              className="conf-fatura-status-card__dot"
                              style={{ background: statusColor }}
                            />
                            <span className="conf-fatura-status-card__copy">
                              <span className="conf-fatura-status-card__label">
                                {aba.label}
                              </span>
                              <span className="conf-fatura-status-card__count">
                                {fmtInt(contarGrupo(aba))}
                              </span>
                            </span>
                          </button>
                        );
                      })}
                    </div>

                    <div
                      className="conf-fatura-status-detail"
                      style={{
                        "--conf-fatura-status-color": ABA_DOT_COLOR[abaAtiva.key],
                      }}
                    >
                      <div className="conf-fatura-status-detail__meta">
                        <span
                          className="conf-fatura-status-detail__dot"
                          style={{ background: ABA_DOT_COLOR[abaAtiva.key] }}
                        />
                        <span className="conf-fatura-status-detail__label">
                          Status selecionado
                        </span>
                      </div>
                      <div className="conf-fatura-status-detail__title">
                        {labelStatusAtivo}
                      </div>
                      <div className="conf-fatura-status-detail__copy">
                        {descricaoStatusAtivo}
                      </div>

                      {pillsVisiveis.length > 0 && (
                        <div className="conf-fatura-pills">
                          {pillsVisiveis.map((p) => {
                            const n = resumoCounts[p.key] || 0;
                            return (
                              <button
                                key={p.key}
                                type="button"
                                title={p.copy}
                                className={`conf-fatura-pill${
                                  filtros.pillSituacao === p.situacao ? " is-active" : ""
                                }`}
                                onClick={() => selecionarPill(p.situacao)}
                                disabled={loading}
                              >
                                {p.label}
                                {n > 0 ? ` · ${fmtInt(n)}` : ""}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </section>

                  <section
                    className="conf-fatura-control-section conf-fatura-control-section--filters"
                    style={{ display: mostrarFiltros ? undefined : "none" }}
                  >
                    <div className="conf-fatura-control-section__head">
                      <div>
                        <div className="conf-fatura-control-section__title">Filtros</div>
                        <div className="conf-fatura-control-section__subtitle">
                          {filtrosAtivosCount > 0
                            ? `${filtrosAtivosCount} filtro(s) avançado(s) ativo(s)`
                            : "Sem filtros avançados ativos"}
                        </div>
                      </div>
                    </div>

                    <div className="conf-fatura-advfilters__body">
                      <div className="conf-fatura-filter-layout">
                        <div className="conf-fatura-filter-group conf-fatura-filter-group--date">
                          <div className="conf-fatura-filter-group__title">Data</div>
                          <div className="conf-fatura-filter-field">
                            <span className="conf-fatura-filter-label">Tipo</span>
                            <div className="conf-fatura-filter-datas__pills">
                              {DATA_CAMPOS.map((d) => (
                                <button
                                  key={d.value}
                                  type="button"
                                  className={`conf-fatura-pill${
                                    filtros.dataCampo === d.value ? " is-active" : ""
                                  }`}
                                  onClick={() => setFiltro("dataCampo", d.value)}
                                  disabled={loading}
                                >
                                  {d.label}
                                </button>
                              ))}
                            </div>
                          </div>
                          <div className="conf-fatura-filter-datas__range">
                            <div className="conf-fatura-filter-field">
                              <label className="conf-fatura-filter-label">De</label>
                              <Input
                                type="date"
                                className="conf-fatura-filter-input"
                                value={filtros.dataDe}
                                onChange={(e) => setFiltro("dataDe", e.target.value)}
                                disabled={loading}
                              />
                            </div>
                            <div className="conf-fatura-filter-field">
                              <label className="conf-fatura-filter-label">Até</label>
                              <Input
                                type="date"
                                className="conf-fatura-filter-input"
                                value={filtros.dataAte}
                                onChange={(e) => setFiltro("dataAte", e.target.value)}
                                disabled={loading}
                              />
                            </div>
                          </div>
                        </div>

                        <div className="conf-fatura-filter-group">
                          <div className="conf-fatura-filter-group__title">
                            Conferência
                          </div>
                          <div className="conf-fatura-filter-stack">
                            <div className="conf-fatura-filter-field">
                              <label className="conf-fatura-filter-label">
                                Possui Divergência
                              </label>
                              <div className="conf-fatura-toggle" role="group">
                                {DIVERGENCIA_OPTS.map((o) => (
                                  <button
                                    key={o.value || "todos"}
                                    type="button"
                                    className={`conf-fatura-toggle__btn${
                                      filtros.divergencia === o.value ? " is-active" : ""
                                    }`}
                                    onClick={() => setFiltro("divergencia", o.value)}
                                    disabled={loading}
                                  >
                                    {o.label}
                                  </button>
                                ))}
                              </div>
                            </div>
                            <div className="conf-fatura-filter-field">
                              <label className="conf-fatura-filter-label">
                                Finalizadas
                              </label>
                              <div className="conf-fatura-toggle" role="group">
                                <button
                                  type="button"
                                  className={`conf-fatura-toggle__btn${
                                    !filtros.mostrarFinalizadas ? " is-active" : ""
                                  }`}
                                  onClick={() => toggleFinalizadas(false)}
                                  disabled={loading}
                                >
                                  Ocultar
                                </button>
                                <button
                                  type="button"
                                  className={`conf-fatura-toggle__btn${
                                    filtros.mostrarFinalizadas ? " is-active" : ""
                                  }`}
                                  onClick={() => toggleFinalizadas(true)}
                                  disabled={loading}
                                >
                                  Mostrar
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="conf-fatura-filter-group conf-fatura-filter-group--lookup">
                          <div className="conf-fatura-filter-group__title">
                            Identificação
                          </div>
                          <div className="conf-fatura-filter-grid">
                            <div className="conf-fatura-filter-field">
                              <label className="conf-fatura-filter-label">
                                Número Interno
                              </label>
                              <Input
                                type="number"
                                className="conf-fatura-filter-input"
                                value={filtros.nrFaturaInt}
                                onChange={(e) => setFiltro("nrFaturaInt", e.target.value)}
                                disabled={loading}
                              />
                            </div>
                            <div className="conf-fatura-filter-field">
                              <label className="conf-fatura-filter-label">Pedido</label>
                              <Input
                                type="text"
                                className="conf-fatura-filter-input"
                                value={filtros.pedido}
                                maxLength={20}
                                placeholder="Ex.: BR26134"
                                onChange={(e) => setFiltro("pedido", e.target.value)}
                                disabled={loading}
                              />
                              {filtros.pedido && filtros.pedido.trim() && (
                                <Button
                                  color="link"
                                  size="sm"
                                  className="p-0 mt-1"
                                  onClick={() => {
                                    const destino = urlAuditoriaPedido(filtros.pedido);
                                    if (history) history.push(destino);
                                    else window.location.assign(destino);
                                  }}
                                >
                                  Ver auditoria do pedido
                                </Button>
                              )}
                            </div>
                            <div className="conf-fatura-filter-field">
                              <label className="conf-fatura-filter-label">Momento</label>
                              <Select
                                className="react-select info"
                                classNamePrefix="react-select"
                                options={MOMENTOS}
                                value={filtros.momento}
                                onChange={(v) => setFiltro("momento", v)}
                                isClearable
                                isDisabled={loading}
                                styles={selectStyles}
                                placeholder="Todos"
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="conf-fatura-filter-actions">
                        <Button
                          size="sm"
                          style={autopilotSecondaryButtonStyle}
                          onClick={handlePesquisar}
                          disabled={loading}
                        >
                          {loading ? <Spinner size="sm" /> : "Aplicar filtros"}
                        </Button>
                        <Button
                          size="sm"
                          color="default"
                          outline
                          onClick={handleLimpar}
                          disabled={loading}
                        >
                          Limpar
                        </Button>
                      </div>
                    </div>
                  </section>
                </div>
              </div>
            </CardBody>
          </Card>

          <div className={`conf-fatura-layout${faturaLateral ? " is-split" : ""}`}>
          <Card
            className="card-everlog-shell"
            style={{ ...autopilotCardStyle, background: "var(--everlog-card-hero-bg)" }}
          >
            <CardBody style={autopilotBodyStyle}>
              {loading && (
                <div className="conf-fatura-loading">
                  <Spinner color="info" />
                </div>
              )}

              {loteResultado && (
                <div className="conf-fatura-lote-resultado" role="status">
                  <div className="conf-fatura-lote-resultado__head">
                    <strong>
                      {rotuloAcao(loteResultado.acao)} em lote:{" "}
                      {fmtInt(loteResultado.itens.filter((r) => r.ok).length)} de{" "}
                      {fmtInt(loteResultado.itens.length)} com sucesso
                    </strong>
                    <button
                      type="button"
                      className="conf-fatura-feedback__fechar"
                      aria-label="Fechar resultado do lote"
                      onClick={() => setLoteResultado(null)}
                    >
                      ×
                    </button>
                  </div>
                  <ul>
                    {loteResultado.itens.map((r) => (
                      <li
                        key={r.idFatura}
                        className={r.ok ? "is-ok" : "is-erro"}
                      >
                        Fatura {r.numero}:{" "}
                        {r.ok
                          ? r.situacaoNova
                            ? labelSituacao(r.situacaoNova)
                            : "ok"
                          : r.erro || "falhou"}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {isEmbarcador && selecionadas.size > 0 && (
                <div
                  className="conf-ctes__massa conf-fatura-lote"
                  role="region"
                  aria-label="Ações nas faturas selecionadas"
                >
                  <span>
                    <b>{fmtInt(selecionadas.size)}</b> fatura(s) selecionada(s)
                  </span>
                  {ACOES_EM_LOTE.filter(
                    (a) => ACOES_LOTE_FIXAS.includes(a) || lotePorAcao[a].length > 0
                  ).map((a) => {
                    const def = ACOES_CONCILIACAO[a] || { label: a };
                    const n = lotePorAcao[a].length;
                    return (
                      <button
                        key={a}
                        type="button"
                        className={`conf-ctes__massa-btn${a === "APROVAR" ? " is-ok" : ""}${
                          def.tom === "danger" ? " is-nok" : ""
                        }`}
                        data-lote={a}
                        onClick={() => executarLote(a)}
                        disabled={executandoLote || n === 0}
                        title={TITULO_LOTE[a]}
                      >
                        {executandoLote && ACOES_CONCILIACAO[a] && ACOES_CONCILIACAO[a].direta ? (
                          <Spinner size="sm" />
                        ) : (
                          `${def.label} (${fmtInt(n)})`
                        )}
                      </button>
                    );
                  })}
                  <span className="conf-ctes__massa-espaco" />
                  <button
                    type="button"
                    className="conf-ctes__massa-btn"
                    onClick={() => setSelecionadas(new Set())}
                  >
                    Desmarcar seleção
                  </button>
                </div>
              )}

              {!loading && error && (
                <div className="conf-fatura-empty">
                  <strong>Erro:</strong>{" "}
                  {typeof error === "object" ? JSON.stringify(error) : error}
                </div>
              )}

              {!loading && !error && consultado && items.length === 0 && (
                <div className="conf-fatura-empty">
                  Nenhuma fatura encontrada com o filtro informado.
                </div>
              )}

              {!loading && !error && items.length > 0 && (
                <>
                  <div className="table-responsive">
                    <Table className="conf-fatura-table" hover>
                      <colgroup>
                        {colunasAtivas.map((col) => (
                          <col key={col.key} className={`conf-fatura-col--${col.key}`} />
                        ))}
                      </colgroup>
                      <thead className="text-primary">
                        <tr>
                          {colunasAtivas.map((col) => (
                            <th
                              key={col.key}
                              className={tableCellClass(col)}
                            >
                              {col.sortField ? (
                                <SortHeader
                                  label={col.label}
                                  field={col.sortField}
                                  sort={sort}
                                  onSort={handleOrdenar}
                                  align={col.num ? "right" : undefined}
                                />
                              ) : (
                                col.label
                              )}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((f) => (
                          <tr
                            key={f.id}
                            className="conf-fatura-row"
                            onClick={(e) => abrirResumoLateral(f, e)}
                          >
                            {colunasAtivas.map((col) => (
                              <td
                                key={col.key}
                                className={tableCellClass(col)}
                              >
                                {col.cell(f)}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                      {totais && (
                        <tfoot>
                          <tr>
                            {colunasAtivas.map((col, idx) => {
                              // "Total" na primeira coluna de conteúdo (a de seleção é estreita).
                              if (idx === colunasAtivas.findIndex((c) => c.key !== "selecao"))
                                return (
                                  <th key={col.key} className={tableCellClass(col)}>
                                    Total
                                  </th>
                                );
                              if (col.total)
                                return (
                                  <th key={col.key} className={tableCellClass(col)}>
                                    {col.total(totais)}
                                  </th>
                                );
                              return <th key={col.key} className={tableCellClass(col)} />;
                            })}
                          </tr>
                        </tfoot>
                      )}
                    </Table>
                  </div>

                  {/* Paginação padrão do Hub (PO1-2724). */}
                  <TablePagination
                    page={page}
                    pageSize={pageSize}
                    total={totalCount}
                    pageSizes={PAGE_SIZES}
                    itemLabel="fatura(s)"
                    disabled={loading}
                    onPageChange={irParaPagina}
                    onPageSizeChange={(novo) => {
                      setPageSize(novo);
                      setPage(1);
                      buscar(1, novo);
                    }}
                  />
                </>
              )}
            </CardBody>
          </Card>

          {faturaLateral && (
            <div className="conf-fatura-panel-col">
              <FaturaResumoLateral
                fatura={faturaLateral}
                onClose={() => setFaturaLateral(null)}
                onVerDetalhes={() => {
                  abrirDetalhe(faturaLateral);
                  setFaturaLateral(null);
                }}
                onVerCtes={() => abrirCtes(faturaLateral, null)}
                onAbrirCte={detalheCteLateral.abrir}
                detalhe={detalheLateral}
                carregandoDetalhe={carregandoDetalhe}
                acoes={acoesDaLinha(faturaLateral)}
                executando={executandoAcao}
                erroAcao={erroAcao}
                onAcao={(a) =>
                  executarAcao(
                    {
                      ...faturaLateral,
                      pendenciaEstrutural: Boolean(
                        faturaLateral.pendenciaEstrutural ||
                          (detalheLateral &&
                            (detalheLateral.pendenciaEstrutural ||
                              (detalheLateral.conferencia && detalheLateral.conferencia.estrutural)))
                      ),
                    },
                    a
                  )
                }
              />
            </div>
          )}
          </div>
        </Col>
      </Row>

      {detalheCteLateral.elemento}
      <CtesModal
        isOpen={modalCtesStatus !== undefined}
        onClose={() => setModalCtesStatus(undefined)}
        fatura={faturaSel}
        status={modalCtesStatus}
        onSaved={handleCtesSalvos}
      />
      <LancamentosModal
        isOpen={modalLanc}
        onClose={() => setModalLanc(false)}
        fatura={faturaSel}
      />
      <FaturaDetalheModal
        isOpen={modalDetalhe}
        onClose={() => setModalDetalhe(false)}
        fatura={faturaSel}
      />
      <ConciliacaoModal
        isOpen={conciliacaoAcao !== null}
        onClose={() => {
          setConciliacaoAcao(null);
          setConciliacaoLote(null);
        }}
        fatura={faturaSel}
        faturas={conciliacaoLote}
        acao={conciliacaoAcao}
        onDone={(resultado, acao) => {
          if (conciliacaoLote) registrarLote(acao, conciliacaoLote, resultado);
          else if (faturaSel) concluirAcao(faturaSel, acao, resultado);
        }}
      />
      <ConciliacoesModal
        isOpen={modalHistorico}
        onClose={() => setModalHistorico(false)}
        fatura={faturaSel}
      />
      <EditarFaturaModal
        isOpen={modalEditar}
        onClose={() => setModalEditar(false)}
        fatura={faturaSel}
        onSaved={() => buscar(page, pageSize)}
      />
      {novaFaturaAberta ? (
        <NovaFaturaModal
          onClose={() => setNovaFaturaAberta(false)}
          onCriada={handleFaturaCriada}
          transportadoraPadrao={transportadoraId}
        />
      ) : null}
      <DeletarFaturaModal
        isOpen={modalDeletar}
        onClose={() => setModalDeletar(false)}
        fatura={faturaSel}
        onDone={() => buscar(page, pageSize)}
      />
    </div>
  );
};

export default ConferenciaFatura;
